import axios from "axios";
import { addDays, addHours, addMinutes, addMonths, addYears } from "date-fns";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AsyncFilterLoadOptions,
  AxiosRequestConfigWithSignal,
  ColumnDefinition,
  DatasetProviderProps,
  DatasetSettings,
  FetchRowsParams,
  FetchRowsResult,
  FilterDefinition,
  FilterGroup,
  ItemForDetailParams,
  OptionType,
  Sort,
} from "../types";
import {
  DataHubClient,
  DataHubDatasetRequestParams,
  ExportFormat,
} from "./DataHubClient";
import { JSONObject } from "@spring-global/spring-datasources/lib/src/Types";
import { cloneDeep } from "lodash";
import { downloadBlob, sanitizeFileName } from "../utils/fileDownload";

export interface ExportProgress {
  isExporting: boolean;
  format: "csv" | "xlsx" | null;
  totalRows: number;
  downloadedRows: number;
  downloadedBytes: number;
  exportError: string | null;
  estimatedTotalBytes: number;
  percentage: number;
  startTime: number | null;
  estimatedTimeRemaining: number | null;
}

export const MAX_AUTO_RETRIES = 10;
export const RETRY_DELAY_SECONDS = 30;

export interface DatasetContextValue {
  filtersDefinition: FilterGroup[];
  getFilterValue: (code: string) => unknown;
  setFilterValue: (code: string, value: unknown) => void;
  getFiltersStateParam: (code: string) => unknown;
  setFiltersStateParam: (code: string, value: unknown) => void;
  requiredFiltersSatisfied: boolean;
  requiredFiltersWarningMessage: string;
  filtersState: Record<string, unknown>;
  filtersDataSource: Record<string, unknown[]>;
  setSort: (sort: Sort | undefined) => void;
  sort: Sort | undefined;
  columnDefinition: ColumnDefinition[];
  count: number | undefined;
  filteredCount: number | undefined;
  hasError: boolean;
  dataSourceId: string;
  isLoading: boolean;
  traceId: string;
  fetchRows: (params: FetchRowsParams) => Promise<FetchRowsResult>;
  resetFilters: () => void;
  filtersVersion: number;
  settings?: DatasetSettings;
  asyncFilterLoadOptions: Record<string, AsyncFilterLoadOptions>;
  selectedItemForDetail: ItemForDetailParams | null;
  setSelectedItemForDetail: (value: ItemForDetailParams | null) => void;
  exportData: (format: ExportFormat) => Promise<void>;
  cancelExport: () => void;
  dismissExportError: () => void;
  exportProgress: ExportProgress;
  isDefinitionLoaded: boolean;
  refreshData: () => void;
  /** Changes on every Refresh; include it in fetch keys to bypass the portal cache. */
  requestId: string | undefined;
  retryCountdown: number;
  retryAttempt: number;
}

const unitMap: Record<string, (date: Date, amount: number) => Date> = {
  d: addDays,
  h: addHours,
  m: addMinutes,
  M: addMonths,
  y: addYears,
};

export const DatasetContext = createContext<DatasetContextValue | null>(null);

export function useDataset(): DatasetContextValue {
  const ctx = useContext(DatasetContext);
  if (!ctx) {
    throw new Error("useDataset must be used within DatasetProvider");
  }
  return ctx;
}

export function DatasetProvider(props: DatasetProviderProps) {
  const [settings, setSettings] = useState<DatasetSettings>();
  const [filtersDataSource, setFiltersDataSource] = useState<
    Record<string, unknown[]>
  >({});
  const [filtersDefinition, setFiltersDefinition] = useState<FilterGroup[]>([]);
  const [filtersState, setFiltersState] = useState<Record<string, unknown>>({});
  const [_filtersStateParam, _setFiltersStateParam] = useState<
    Record<string, unknown>
  >({});
  const [columnDefinition, setColumnDefinition] = useState<ColumnDefinition[]>(
    [],
  );

  const [
    selectedItemForDetail,
    setSelectedItemForDetail,
  ] = useState<ItemForDetailParams | null>(null);

  const [exportProgress, setExportProgress] = useState<ExportProgress>({
    isExporting: false,
    format: null,
    totalRows: 0,
    downloadedRows: 0,
    downloadedBytes: 0,
    estimatedTotalBytes: 0,
    percentage: 0,
    startTime: null,
    estimatedTimeRemaining: null,
    exportError: null,
  });

  const exportAbortControllerRef = useRef<AbortController | null>(null);
  const exportResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const cacheTokenKey = `dataset_${props.dataSourceId}_requestId`;

  const [requestId, setRequestId] = useState<string | undefined>(
    () => localStorage.getItem(cacheTokenKey) || undefined,
  );

  const refreshData = useCallback(() => {
    const newId = crypto.randomUUID();
    localStorage.setItem(cacheTokenKey, newId);
    setRequestId(newId);
    setFiltersDataSource({});
    setError(false);
  }, [cacheTokenKey]);

  // Ctrl+F5 / Ctrl+Shift+R: set a fresh _requestId before the hard reload
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isHardRefresh =
        (e.ctrlKey || e.metaKey) &&
        (e.key === "F5" || (e.shiftKey && e.key === "r"));
      if (isHardRefresh) {
        localStorage.setItem(cacheTokenKey, crypto.randomUUID());
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [cacheTokenKey]);

  const [sort, setSort] = useState<Sort | undefined>();
  const [count, setCount] = useState<number | undefined>(0);
  const [filteredCount, setFilteredCount] = useState<number | undefined>(0);
  const [hasError, setError] = useState(false);
  const [traceId, setTraceId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isDefinitionLoaded, setDefinitionLoaded] = useState(false);
  const [requiredFiltersSatisfied, setRequiredFiltersSatisfied] = useState(
    false,
  );
  const [
    requiredFiltersWarningMessage,
    setRequiredFiltersWarningMessage,
  ] = useState("");

  const [retryCountdown, setRetryCountdown] = useState(0);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const retryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const getFiltersStateParam = useCallback(
    (code: string): unknown => _filtersStateParam[code] ?? "",
    [_filtersStateParam],
  );

  const setFiltersStateParam = useCallback((code: string, value: unknown) => {
    _setFiltersStateParam((prev) => ({
      ...prev,
      [code]: value,
    }));
  }, []);

  const getFilterValue = useCallback((code: string) => filtersState[code], [
    filtersState,
  ]);

  const setFilterValue = useCallback((code: string, value: unknown) => {
    setFiltersState((prev) => ({
      ...prev,
      [code]: value,
    }));
  }, []);

  const parseDateExpression = (
    value: Date | number | string,
  ): Date | undefined => {
    if (value instanceof Date) return value;
    if (typeof value === "number") return new Date(value);
    if (typeof value !== "string") return undefined;

    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }

    const match = value.match(/^([+-])(\d+)([a-zA-Z])$/);
    if (!match) return undefined;

    const [, sign, amount, unit] = match;
    const fn = unitMap[unit as keyof typeof unitMap];
    if (!fn) return undefined;

    const delta = sign === "-" ? -Number(amount) : Number(amount);
    return fn(new Date(), delta);
  };

  const parseInitialValue = useCallback((filter: FilterDefinition): unknown => {
    const value = filter.initialValue;
    if (value == null) return undefined;

    switch (filter.dataType) {
      case "number": {
        const num = Number(value);
        return isNaN(num) ? undefined : num;
      }

      case "string[]":
        return Array.isArray(value)
          ? value.map((v) => String(v ?? "").trim())
          : [];

      case "number[]":
        return Array.isArray(value)
          ? value.map(Number).filter((n) => !isNaN(n))
          : [];

      case "date":
        return parseDateExpression(value as Date | number | string);

      case "date[]":
        if (Array.isArray(value) && value.length >= 2) {
          return [parseDateExpression(value[0]), parseDateExpression(value[1])];
        }
        return [];
      default:
        return String(value ?? "").trim();
    }
  }, []);

  const updateFiltersInitialValues = useCallback(
    (fg: FilterGroup[] | undefined): void => {
      if (!Array.isArray(fg) || fg.length === 0) return;

      const initialValues: Record<string, unknown> = {};
      fg.flatMap((g) => g.filters).forEach((f) => {
        if (f.initialValue !== undefined) {
          initialValues[f.code] = parseInitialValue(f);
        }
      });

      setFiltersState((prev) => ({
        ...prev,
        ...initialValues,
      }));
    },
    [parseInitialValue],
  );

  const updateSettings = (s: DatasetSettings | undefined): void => {
    const value: DatasetSettings = s ?? {};
    value.grid = value.grid ?? {};
    value.grid.cacheBlockSize = value.grid.cacheBlockSize ?? 100;
    value.grid.maxBlocksInCache = value.grid.maxBlocksInCache ?? 10;
    setSettings(value);
  };

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearInterval(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setRetryCountdown(0);
  }, []);

  const handleRequestError = (e: unknown): void => {
    if (axios.isAxiosError(e) && e.code === "ERR_CANCELED") return;

    setError(true);
    if (axios.isAxiosError(e)) {
      setTraceId(e.response?.data?.traceId || "");
    }

    // Auto-retry with countdown
    setRetryAttempt((prev) => {
      const next = prev + 1;
      if (next <= MAX_AUTO_RETRIES) {
        setRetryCountdown(RETRY_DELAY_SECONDS);
        if (retryTimerRef.current) clearInterval(retryTimerRef.current);
        retryTimerRef.current = setInterval(() => {
          setRetryCountdown((c) => {
            if (c <= 1) {
              // Stop the timer — use setTimeout to avoid nested state conflicts
              if (retryTimerRef.current) {
                clearInterval(retryTimerRef.current);
                retryTimerRef.current = null;
              }
              setTimeout(() => refreshData(), 0);
              return 0;
            }
            return c - 1;
          });
        }, 1000);
      }
      return next;
    });
  };

  const resetFilters = useCallback(() => {
    setFiltersState({});
    updateFiltersInitialValues(filtersDefinition);
  }, [filtersDefinition, updateFiltersInitialValues]);

  const isEmpty = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "string") return value.trim().length === 0;
    return value == null;
  };

  const sanitizedFiltersState = useMemo(() => {
    let newState = cloneDeep(filtersState);

    const allFilters = filtersDefinition.flatMap((f) => f.filters);

    allFilters
      .filter((f) => f.type === "search")
      .forEach((f) => {
        const val = newState[f.code];
        if (typeof val === "string") {
          const minLen =
            typeof f.args?.minLength === "number" ? f.args.minLength : 3;
          if (val.trim().length < minLen) {
            delete (newState as Record<string, unknown>)[f.code];
          }
        }
      });

    allFilters
      .filter((f) => f.type === "asyncMultiSelect")
      .forEach((f) => {
        const val = newState[f.code];
        if (Array.isArray(val)) {
          // Selections made in the picker arrive as { value, label }, but a job
          // can declare `initialValue` on an async filter and those arrive as
          // raw scalars (parseInitialValue returns plain strings for
          // "string[]"). Reading `.value` off a scalar yields undefined, which
          // serialises to null and reaches the job as a bogus filter value —
          // mixed in with the real ones once the user adds a selection on top.
          // Same defensive shape the multiSelect branch below already uses.
          newState[f.code] = val.map((v: unknown) =>
            typeof v === "object" && v !== null && "value" in v
              ? (v as OptionType).value
              : v,
          );
        }
      });

    allFilters
      .filter((f) => f.type === "multiSelect")
      .forEach((f) => {
        const val = newState[f.code];
        if (Array.isArray(val)) {
          newState[f.code] = val.map((v: any) =>
            typeof v === "object" && v !== null && "id" in v ? v.id : v,
          );
        }
      });

    allFilters
      .filter((f) => f.type === "dateRange")
      .forEach((f) => {
        const val = newState[f.code];
        if (!Array.isArray(val)) return;
        newState[f.code] = (val as [string, string]).map(
          (v: string, index: number) => {
            const d = new Date(v);
            const year = d.getFullYear();
            const month = d.getMonth();
            const day = d.getDate();
            if (index === 0) {
              return new Date(
                Date.UTC(year, month, day, 0, 0, 0, 0),
              ).toISOString();
            } else {
              return new Date(
                Date.UTC(year, month, day, 23, 59, 59, 0),
              ).toISOString();
            }
          },
        );
      });

    return Object.fromEntries(
      Object.entries(newState).filter(([_, v]) => !isEmpty(v)),
    );
  }, [filtersDefinition, filtersState]);

  const [debouncedFilters, setDebouncedFilters] = useState(
    sanitizedFiltersState,
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFilters((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(sanitizedFiltersState)) {
          return prev;
        }
        return sanitizedFiltersState;
      });
    }, 500);

    return () => clearTimeout(t);
  }, [sanitizedFiltersState]);

  // On first definition load, fast-forward debouncedFilters to the current
  // sanitized state so dependent-filter refetches don't fire once with empty
  // parent values and again 500ms later when the debounce settles.
  const initialDebounceSyncedRef = useRef(false);
  useEffect(() => {
    if (isDefinitionLoaded && !initialDebounceSyncedRef.current) {
      initialDebounceSyncedRef.current = true;
      setDebouncedFilters(sanitizedFiltersState);
    }
  }, [isDefinitionLoaded, sanitizedFiltersState]);

  // Mirror of debouncedFilters available to async loadOptions callbacks without
  // forcing them to be recreated on every filter change.
  const debouncedFiltersRef = useRef<Record<string, unknown>>(debouncedFilters);
  useEffect(() => {
    debouncedFiltersRef.current = debouncedFilters;
  }, [debouncedFilters]);

  // Mirror of raw filtersState used by reactive refetch to trim stale
  // selections after dependent filter options refresh.
  const filtersStateRef = useRef<Record<string, unknown>>(filtersState);
  useEffect(() => {
    filtersStateRef.current = filtersState;
  }, [filtersState]);

  const filterDependencyInfo = useMemo(() => {
    const all = filtersDefinition
      .flatMap((g) => g.filters)
      .filter((f) => !!f.dataSource);
    const dependent = all.filter(
      (f) => Array.isArray(f.dependsOn) && f.dependsOn.length > 0,
    );
    const independent = all.filter(
      (f) => !Array.isArray(f.dependsOn) || f.dependsOn.length === 0,
    );
    return { all, dependent, independent };
  }, [filtersDefinition]);

  // Stringified parent values, per dependent filter. When this changes, the
  // affected dependent filters need to refetch.
  const dependentFiltersTriggerKey = useMemo(() => {
    const map: Record<string, unknown[]> = {};
    filterDependencyInfo.dependent.forEach((f) => {
      map[f.code] = (f.dependsOn ?? []).map((p) => debouncedFilters[p]);
    });
    return JSON.stringify(map);
  }, [filterDependencyInfo.dependent, debouncedFilters]);

  const [filtersVersion, setFiltersVersion] = useState(0);

  useEffect(() => {
    setFiltersVersion((v) => v + 1);
  }, [debouncedFilters, requestId]);

  useEffect(() => {
    if (!isDefinitionLoaded) {
      return;
    }

    const reqOneOf = settings?.filter?.requirements?.oneOf ?? [];

    const hasMinimumFilters =
      !Array.isArray(reqOneOf) ||
      reqOneOf.length === 0 ||
      reqOneOf.some((r) => !isEmpty(filtersState[r]));

    setRequiredFiltersSatisfied(hasMinimumFilters);

    if (hasMinimumFilters) {
      setRequiredFiltersWarningMessage("");
      return;
    }

    const filterNames = filtersDefinition
      .flatMap((f) => f.filters)
      .filter((f) => reqOneOf?.includes(f.code))
      .map((f) => `"${f.name}"`)
      .join(", ");

    const warningMessage =
      settings?.filter?.requirements?.warningMessage ??
      `Please, select at least one of the filters: ${filterNames}.`;

    setRequiredFiltersWarningMessage(warningMessage);
  }, [filtersDefinition, filtersState, settings, isDefinitionLoaded]);

  const fetchRows = useCallback(
    async ({ startRow, endRow, sort: paramSort }: FetchRowsParams) => {
      const previousController = abortControllerRef.current;

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setError(false);

      // Very small delay before aborting to avoid CORS race condition
      // but not too long to avoid duplicate requests
      if (previousController) {
        setTimeout(() => previousController.abort(), 5);
      }

      try {
        const top = endRow - startRow;
        const skip = startRow;
        const currentSort = paramSort ?? sort;
        const params: DataHubDatasetRequestParams = {
          publicId: props.dataSourceId,
          top,
          skip,
          sortColumn: currentSort?.columnId,
          sortDirection: currentSort?.direction,
          filtersState: debouncedFilters,
          _requestId: requestId,
        };

        const res = !requiredFiltersSatisfied
          ? { count: 0, filteredCount: 0, rows: [] }
          : await DataHubClient.getDataset(params, {
              signal: controller.signal,
            } as AxiosRequestConfigWithSignal);

        setCount(res.count);
        setFilteredCount(res.filteredCount);

        // Reset retry state on success
        setRetryAttempt(0);
        clearRetryTimer();

        return {
          rows: res.rows ?? [],
          total: res.filteredCount ?? 0,
        };
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.code === "ERR_CANCELED") {
          return { rows: [], total: 0, canceled: true };
        }

        handleRequestError(e);
        return { rows: [], total: 0 };
      } finally {
        setIsLoading(false);
      }
    },
    [
      sort,
      props.dataSourceId,
      debouncedFilters,
      requiredFiltersSatisfied,
      requestId,
    ],
  );

  const fetchAsyncFilterData = useCallback(
    async (
      datasourceId: string,
      search: string,
      skip: number,
      bufferSize: number,
      args: JSONObject,
      signal?: AbortSignal,
    ) => {
      const payload = {
        _id: datasourceId,
        _requestId: requestId,
        args: {
          search,
          skip,
          top: bufferSize,
          ...(args ?? {}),
        },
      };
      const res = await DataHubClient.getAsyncFilterData(payload, { signal });
      return { data: res?.rows ?? [] };
    },
    [requestId],
  );

  const cancelExport = useCallback(() => {
    if (exportAbortControllerRef.current) {
      exportAbortControllerRef.current.abort();
      exportAbortControllerRef.current = null;
    }
    if (exportResetTimeoutRef.current) {
      clearTimeout(exportResetTimeoutRef.current);
      exportResetTimeoutRef.current = null;
    }
    setExportProgress({
      isExporting: false,
      format: null,
      totalRows: 0,
      downloadedRows: 0,
      downloadedBytes: 0,
      estimatedTotalBytes: 0,
      percentage: 0,
      startTime: null,
      estimatedTimeRemaining: null,
      exportError: null,
    });
  }, []);

  const dismissExportError = useCallback(() => {
    setExportProgress((prev) => ({ ...prev, exportError: null }));
  }, []);

  useEffect(() => {
    return () => {
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
      }
      if (exportResetTimeoutRef.current) {
        clearTimeout(exportResetTimeoutRef.current);
      }
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current);
      }
    };
  }, []);

  const exportData = useCallback(
    async (format: ExportFormat): Promise<void> => {
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
      }

      if (exportResetTimeoutRef.current) {
        clearTimeout(exportResetTimeoutRef.current);
        exportResetTimeoutRef.current = null;
      }

      const abortController = new AbortController();
      exportAbortControllerRef.current = abortController;

      const startTime = Date.now();
      setExportProgress({
        isExporting: true,
        format,
        totalRows: 0,
        downloadedRows: 0,
        downloadedBytes: 0,
        estimatedTotalBytes: 0,
        percentage: 0,
        startTime,
        estimatedTimeRemaining: null,
        exportError: null,
      });

      try {
        const response = await DataHubClient.exportDataset(
          {
            publicId: props.dataSourceId,
            sortColumn: sort?.columnId,
            sortDirection: sort?.direction,
            filtersState: debouncedFilters,
            format,
            fileName: props.title,
            columns:
              settings?.export?.fields != null
                ? Object.entries(
                    settings.export.fields,
                  ).map(([field, headerName]) => ({ field, headerName }))
                : columnDefinition
                    .filter((col) => col.type !== "actions")
                    .map((col) => ({
                      field: col.field ?? "",
                      headerName: col.headerName ?? col.field ?? "",
                    })),
          },
          abortController.signal,
        );

        if (!response.ok) {
          let errorMessage: string;
          try {
            const errorJson = await response.json();
            errorMessage =
              errorJson.error || `Export failed (HTTP ${response.status})`;
          } catch {
            errorMessage = `Export failed (HTTP ${response.status})`;
          }
          throw new Error(errorMessage);
        }

        const totalRows = parseInt(
          response.headers.get("X-Total-Rows") || "0",
          10,
        );
        const pageSize = parseInt(
          response.headers.get("X-Page-Size") || "4000",
          10,
        );
        const totalPages = Math.ceil(totalRows / pageSize);
        // The server already returns an OS-safe base name; sanitize again so a missing
        // header (e.g. not exposed by CORS) can never produce a file name with ":" in it,
        // which Windows cannot extract from an archive.
        const exportFileName = sanitizeFileName(
          response.headers.get("X-Export-Filename") ||
            response.headers.get("X-Export-Timestamp") ||
            new Date().toISOString(),
        );

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No reader available");

        const chunks: BlobPart[] = [];
        let downloadedBytes = 0;
        let downloadedRows = 0;
        let dynamicAvgBytesPerRow = 200;
        const textDecoder = format === "csv" ? new TextDecoder() : null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          chunks.push(new Blob([value]));
          downloadedBytes += value.length;

          if (textDecoder) {
            const text = textDecoder.decode(value, { stream: true });
            const newlines = (text.match(/\r?\n/g) || []).length;
            downloadedRows += newlines;

            if (downloadedRows > 100) {
              dynamicAvgBytesPerRow = downloadedBytes / downloadedRows;
            }
          }

          const estimatedTotalBytes = totalRows * dynamicAvgBytesPerRow;

          const percentage = Math.min(
            99,
            Math.round((downloadedBytes / estimatedTotalBytes) * 100),
          );
          const elapsed = Date.now() - startTime;
          const bytesPerMs = downloadedBytes / elapsed;
          const remainingBytes = Math.max(
            0,
            estimatedTotalBytes - downloadedBytes,
          );
          const estimatedTimeRemaining = Math.round(
            remainingBytes / bytesPerMs / 1000,
          );

          let displayedRows: number;
          if (format === "csv") {
            displayedRows = Math.max(0, downloadedRows - 1);
          } else {
            const estimatedCurrentPage = Math.floor(
              (downloadedBytes / estimatedTotalBytes) * totalPages,
            );
            displayedRows = Math.min(
              totalRows,
              estimatedCurrentPage * pageSize,
            );
          }

          setExportProgress({
            isExporting: true,
            format,
            totalRows,
            downloadedRows: displayedRows,
            downloadedBytes,
            estimatedTotalBytes,
            percentage,
            startTime,
            estimatedTimeRemaining,
            exportError: null,
          });
        }

        if (format === "xlsx") {
          // XLSX already arrives zipped from the server (the archive is what gets streamed).
          const zipBlob = new Blob(chunks, { type: "application/zip" });
          downloadBlob(zipBlob, `${exportFileName}.zip`);
        } else {
          // CSV is delivered as-is: zipping it here saved no bandwidth (the response is
          // already gzipped on the wire) and only forced the user to extract it before
          // Excel could open the file.
          const csvBlob = new Blob(chunks, { type: "text/csv;charset=utf-8" });
          downloadBlob(csvBlob, `${exportFileName}.csv`);
        }

        setExportProgress((prev) => ({ ...prev, percentage: 100 }));
        exportResetTimeoutRef.current = setTimeout(() => {
          setExportProgress({
            isExporting: false,
            format: null,
            totalRows: 0,
            downloadedRows: 0,
            downloadedBytes: 0,
            estimatedTotalBytes: 0,
            percentage: 0,
            startTime: null,
            estimatedTimeRemaining: null,
            exportError: null,
          });
        }, 1500);
      } catch (err) {
        if (err instanceof Error && err.name !== "AbortError") {
          const message = err.message;
          setExportProgress((prev) => ({
            ...prev,
            isExporting: false,
            exportError: message,
          }));
        }
      }
    },
    [
      props.dataSourceId,
      props.title,
      sort,
      debouncedFilters,
      columnDefinition,
      settings?.export?.fields,
    ],
  );

  const asyncFilterLoadOptionsMap = useMemo<
    Record<string, AsyncFilterLoadOptions>
  >(() => {
    const PAGE_SIZE = 100;
    const filterList = filtersDefinition
      .flatMap((f) => f.filters)
      .filter((f) => !!f.dataSource && f.type === "asyncMultiSelect");

    const map: Record<string, AsyncFilterLoadOptions> = {};
    const abortControllers: Record<string, AbortController | null> = {};

    for (const filter of filterList) {
      map[filter.code] = async (search, _, additional) => {
        const page = additional?.page ?? 1;
        const skip = (page - 1) * PAGE_SIZE;

        abortControllers[filter.code]?.abort();
        const controller = new AbortController();
        abortControllers[filter.code] = controller;

        // Merge static args with current values of parent filters (dependsOn).
        // Parent values are read from a ref so the loadOptions reference does
        // not change with every filter edit (AsyncPaginate is keyed on
        // `filtersVersion`, which already re-mounts the picker on debounced
        // changes).
        const dynamicArgs: JSONObject = { ...(filter.args ?? {}) };
        const current = debouncedFiltersRef.current ?? {};
        (filter.dependsOn ?? []).forEach((parentCode) => {
          if (current[parentCode] !== undefined) {
            dynamicArgs[parentCode] = current[parentCode] as JSONObject[string];
          }
        });

        // loadOptions must never reject: AsyncPaginate keeps the menu in its
        // loading state forever when it does, which reads to the user as a
        // filter that "spins and never loads". Every failure — including the
        // abort above, which rejects the superseded request — has to resolve
        // into an option list instead.
        let rows: unknown[];
        try {
          const res = await fetchAsyncFilterData(
            String(filter.dataSource),
            search,
            skip,
            PAGE_SIZE + 1,
            dynamicArgs,
            controller.signal,
          );
          rows = res.data;
        } catch (err) {
          if (axios.isAxiosError(err) && err.code === "ERR_CANCELED") {
            // Superseded by a newer keystroke; keep the page so the next
            // request resumes from the same offset.
            return { options: [], hasMore: false, additional: { page } };
          }
          console.warn(
            `[DatasetProvider] Filter "${filter.code}" could not load its options.`,
            err,
          );
          return { options: [], hasMore: false, additional: { page } };
        }

        const hasMore = rows.length > PAGE_SIZE;
        const options = rows.slice(0, PAGE_SIZE).map((item: any) => ({
          value: item.id,
          label: item.text,
        }));

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      };
    }

    return map;
  }, [filtersDefinition, fetchAsyncFilterData]);

  // Fetch independent (no dependsOn) multiSelect filter options once on mount /
  // on definition load. These do not react to other filter values.
  useEffect(() => {
    if (!props.dataSourceId || filtersDefinition.length === 0) {
      setFiltersDataSource({});
      return;
    }

    const list = filterDependencyInfo.independent.filter(
      (f) => f.type !== "asyncMultiSelect",
    );
    if (list.length === 0) return;

    const fetchIndependent = async () => {
      const results: Record<string, unknown[]> = {};

      try {
        const fetchFiltersData = list.map((f) => f.dataSource).join(",");

        const res = await DataHubClient.getDataset({
          publicId: props.dataSourceId,
          fetchFiltersData,
          _requestId: requestId,
        });
        const resFilterData = (res as unknown) as Record<string, unknown[]>;
        list.forEach((f: FilterDefinition) => {
          results[f.code] = resFilterData[String(f.dataSource)] ?? [];
        });
      } catch {
        list.forEach((f) => {
          results[f.code] = [];
        });
      }

      setFiltersDataSource((prev) => ({ ...prev, ...results }));
    };

    fetchIndependent();
  }, [
    props.dataSourceId,
    filtersDefinition,
    requestId,
    filterDependencyInfo.independent,
  ]);

  // Refetch dependent multiSelect filter options whenever the values of their
  // declared parent filters change. Trims selections that disappeared from the
  // refreshed option set so the persisted state does not carry stale ids.
  useEffect(() => {
    if (!props.dataSourceId) return;

    const list = filterDependencyInfo.dependent.filter(
      (f) => f.type === "multiSelect",
    );
    if (list.length === 0) return;

    const controller = new AbortController();

    const fetchDependent = async () => {
      try {
        const fetchFiltersData = list.map((f) => f.dataSource).join(",");

        const parentCodes = new Set<string>();
        list.forEach((f) => f.dependsOn?.forEach((c) => parentCodes.add(c)));
        const currentDebounced = debouncedFiltersRef.current ?? {};
        const filtersStateForFetch: Record<string, unknown> = {};
        parentCodes.forEach((c) => {
          if (currentDebounced[c] !== undefined) {
            filtersStateForFetch[c] = currentDebounced[c];
          }
        });

        const res = await DataHubClient.getDataset(
          {
            publicId: props.dataSourceId,
            fetchFiltersData,
            filtersState: filtersStateForFetch,
            _requestId: requestId,
          },
          { signal: controller.signal } as AxiosRequestConfigWithSignal,
        );

        const resFilterData = (res as unknown) as Record<string, unknown[]>;
        const updates: Record<string, unknown[]> = {};
        list.forEach((f) => {
          updates[f.code] = resFilterData[String(f.dataSource)] ?? [];
        });

        setFiltersDataSource((prev) => ({ ...prev, ...updates }));

        list.forEach((f) => {
          const opts =
            (updates[f.code] as Array<{ id: number | string }>) ?? [];
          const currentSelected = filtersStateRef.current[f.code];
          if (!Array.isArray(currentSelected) || currentSelected.length === 0) {
            return;
          }
          const validIds = new Set(opts.map((o) => String(o.id)));
          const filtered = currentSelected.filter((v: unknown) => {
            const id =
              typeof v === "object" && v !== null && "id" in v
                ? (v as { id: number | string }).id
                : (v as number | string);
            return validIds.has(String(id));
          });
          if (filtered.length !== currentSelected.length) {
            setFilterValue(f.code, filtered);
          }
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.code === "ERR_CANCELED") return;
        console.warn(
          "[DatasetProvider] Failed to refetch dependent filters; keeping previous options.",
          err,
        );
      }
    };

    fetchDependent();

    return () => {
      controller.abort();
    };
  }, [
    props.dataSourceId,
    requestId,
    dependentFiltersTriggerKey,
    filterDependencyInfo.dependent,
    setFilterValue,
  ]);

  useEffect(() => {
    DataHubClient.getDataset({
      publicId: props.dataSourceId,
      fetchDefinition: true,
      _requestId: requestId,
    })
      .then((res) => {
        setFiltersDefinition(res.filters);
        setColumnDefinition(res.columns ?? []);
        updateFiltersInitialValues(res.filters);
        updateSettings(res.settings);
        setDefinitionLoaded(true);
      })
      .catch(handleRequestError);
  }, [parseInitialValue, props.dataSourceId, updateFiltersInitialValues, requestId]);

  const value: DatasetContextValue = useMemo(
    () => ({
      columnDefinition,
      filtersDefinition,
      count,
      filteredCount,
      dataSourceId: props.dataSourceId,
      isLoading,
      hasError,
      traceId,
      sort,
      fetchRows,
      setSort,
      getFilterValue,
      setFilterValue,
      filtersState,
      filtersDataSource,
      resetFilters,
      settings,
      asyncFilterLoadOptions: asyncFilterLoadOptionsMap,
      setFiltersStateParam,
      getFiltersStateParam,
      filtersVersion,
      requiredFiltersSatisfied,
      requiredFiltersWarningMessage,
      selectedItemForDetail,
      setSelectedItemForDetail,
      exportData,
      cancelExport,
      dismissExportError,
      exportProgress,
      isDefinitionLoaded,
      refreshData,
      requestId,
      retryCountdown,
      retryAttempt,
    }),
    [
      columnDefinition,
      filtersDefinition,
      count,
      filteredCount,
      props.dataSourceId,
      isLoading,
      hasError,
      traceId,
      sort,
      fetchRows,
      getFilterValue,
      setFilterValue,
      filtersState,
      filtersDataSource,
      resetFilters,
      settings,
      asyncFilterLoadOptionsMap,
      setFiltersStateParam,
      getFiltersStateParam,
      filtersVersion,
      requiredFiltersSatisfied,
      requiredFiltersWarningMessage,
      selectedItemForDetail,
      setSelectedItemForDetail,
      exportData,
      cancelExport,
      dismissExportError,
      exportProgress,
      isDefinitionLoaded,
      refreshData,
      requestId,
      retryCountdown,
      retryAttempt,
    ],
  );

  return (
    <DatasetContext.Provider value={value}>
      {props.children}
    </DatasetContext.Provider>
  );
}
